/**
 * Converts any case (camelCase, snake_case, kebab-case, SCREAMING_SNAKE)
 * into proper Title Case for display.
 */

const ACRONYMS = new Set(["ai", "icp", "url", "api", "sdk", "crm", "saas", "b2b", "b2c", "ceo", "cto", "cfo", "vp", "hr", "it", "us", "uk"]);

export const formatDisplayText = (text) => {
  if (!text || typeof text !== "string") return text || "";

  const words = text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/);

  return words
    .map((word) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return word.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const formatWeight = (weight) => {
  if (!weight) return "";
  return formatDisplayText(weight);
};

export const formatConfidence = (confidence) => {
  if (!confidence) return "";
  const lower = confidence.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Format API answer values
 * "NO_EVIDENCE" -> "No Evidence Available"
 * "N/A" -> "Not Available"
 * Normal text passes through with first letter capitalized
 */
const ANSWER_REPLACEMENTS = {
  no_evidence: "No Evidence Available",
  "n/a": "Not Available",
  none: "None Found",
  unknown: "Unknown",
  not_found: "Not Found",
  not_available: "Not Available",
};

// Sentinel values that should be hidden entirely when they appear as evidence text
const EVIDENCE_SENTINELS = new Set([
  "no_evidence",
  "n/a",
  "none",
  "unknown",
  "not_found",
  "not_available",
  "no evidence",
  "no evidence available",
]);

export const formatAnswer = (answer) => {
  if (!answer || typeof answer !== "string") return answer || "";
  const key = answer.toLowerCase().replace(/[_-]+/g, "_").trim();
  if (ANSWER_REPLACEMENTS[key]) return ANSWER_REPLACEMENTS[key];
  return formatDisplayText(answer);
};

/**
 * Clean evidence text:
 * - Replaces markdown links ([text](url)) with just the URL
 * - Removes broken parenthetical patterns like ([domain.com](domain.com\n))
 * - Returns clean text with URLs separated out
 */
export const cleanEvidenceText = (text) => {
  if (!text || typeof text !== "string") return { cleanText: "", urls: [] };

  // If the entire evidence value is just a sentinel, suppress it
  if (EVIDENCE_SENTINELS.has(text.toLowerCase().trim())) return { cleanText: "", urls: [] };

  const urls = [];

  // Extract URLs from markdown links: [text](url) or ([text](url))
  let cleaned = text.replace(/\(?\[([^\]]*)\]\(([^)\s]+)\s*\)\)?/g, (match, label, url) => {
    const cleanUrl = url.replace(/\n/g, "").trim();
    if (cleanUrl && cleanUrl.length > 3) {
      const fullUrl = cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`;
      if (!urls.find((u) => u.url === fullUrl)) {
        urls.push({ url: fullUrl, domain: extractDomainClean(fullUrl) });
      }
    }
    return "";
  });

  // Also catch raw URLs in text
  const rawUrlRegex = /https?:\/\/[^\s)}\]]+/g;
  let m;
  while ((m = rawUrlRegex.exec(cleaned)) !== null) {
    const url = m[0];
    if (!urls.find((u) => u.url === url)) {
      urls.push({ url, domain: extractDomainClean(url) });
    }
  }

  // Clean up leftover parentheses and whitespace
  cleaned = cleaned
    .replace(/\(\s*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+\./g, ".")
    .replace(/\s+,/g, ",")
    .trim();

  return { cleanText: cleaned, urls };
};

const extractDomainClean = (url) => {
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    return u.hostname.replace("www.", "");
  } catch {
    return url;
  }
};
